import {
  JobPlacementHistory,
  JobPlacementStatus,
} from '@company/types/job-placement.type';
import DATE_FORMATS from '@constants/dateFormat';
import {
  Badge,
  Collapse,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { formatLocalDate } from '@utils/DateUtils';
import { getJobPlacementStatusVariant } from '@utils/statusColors';
import { format } from 'date-fns';
import { useState } from 'react';
import { BiChevronDown, BiChevronRight } from 'react-icons/bi';

interface JobWorkerShiftsHistoryProps {
  jobPlacementHistoryList: JobPlacementHistory[];
  jobTimezone?: string;
}

export default function WorkerShiftsHistory({
  jobPlacementHistoryList,
  jobTimezone,
}: JobWorkerShiftsHistoryProps) {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const toggleExpanded = (id: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const ACTION_TEXTS: Record<JobPlacementStatus, string> = {
    [JobPlacementStatus.OFFERED]: 'Offered',
    [JobPlacementStatus.ACCEPTED]: 'Accepted',
    [JobPlacementStatus.OFFER_UPDATED]: 'Updated Offer',
    [JobPlacementStatus.SHIFTS_UPDATED]: 'Updated Shifts',
    [JobPlacementStatus.CANCELLED]: 'Cancelled',
    [JobPlacementStatus.DECLINED]: 'Declined',
    [JobPlacementStatus.DECLINED_ADDITIONAL]: 'Declined Additional',
    [JobPlacementStatus.HIRED]: 'Hired',
  };

  const getActionText = (historyItem: JobPlacementHistory): string => {
    return ACTION_TEXTS[historyItem.status] || 'Unknown';
  };

  const renderShiftDetails = (historyItem: JobPlacementHistory) => {
    if (!historyItem.shiftItems || historyItem.shiftItems.length === 0) {
      return null;
    }

    return (
      <Stack gap="xs" mt="xs">
        {historyItem.shiftItems.map((shift) => (
          <div key={`${shift.id}-${shift.startTime}-${shift.position}`}>
            <Text size="xs" c="dimmed" mb="xs" fw={500}>
              Shift Date:{' '}
              {formatLocalDate(
                new Date(shift.startTime),
                DATE_FORMATS.DATE_FORMAT,
                jobTimezone
              )}
              {', '}
              {formatLocalDate(
                new Date(shift.startTime),
                DATE_FORMATS.TIME_FORMAT,
                jobTimezone
              )}
              {' - '}
              {formatLocalDate(
                new Date(shift.endTime),
                DATE_FORMATS.TIME_FORMAT,
                jobTimezone
              )}
            </Text>
            <Text size="xs" c="dimmed" mb="xs">
              <Text component="span" fw={500}>
                Pay rate: {shift.payRate}/{shift.payRatePeriod?.toLowerCase()}
              </Text>
            </Text>
          </div>
        ))}
      </Stack>
    );
  };

  if (!jobPlacementHistoryList || jobPlacementHistoryList.length === 0) {
    return (
      <Paper p="md">
        <Text fw={600} size="sm" mb="sm">
          Job Shifts History
        </Text>
        <Text size="sm" c="dimmed">
          No shift history available for this job.
        </Text>
      </Paper>
    );
  }

  return (
    <Paper p="md">
      <Text fw={600} size="sm" mb="md">
        Job Shifts History
      </Text>
      <Stack gap="sm">
        {jobPlacementHistoryList.map((history) => {
          const isExpanded = expandedItems.has(history.id);
          const hasShiftDetails =
            history.shiftItems && history.shiftItems.length > 0;

          return (
            <div key={history.id}>
              <Group justify="space-between" align="center" wrap="nowrap">
                <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
                  <Text fw={500} size="sm" style={{ minWidth: '80px' }}>
                    {formatLocalDate(
                      history.dateCreated,
                      DATE_FORMATS.DATE_FORMAT
                    )}
                  </Text>
                  <Text
                    size="sm"
                    style={{ flex: 1, textTransform: 'capitalize' }}
                  >
                    {history.updatedBy} {getActionText(history)?.toLowerCase()}{' '}
                    at{' '}
                    <Text component="span" c="dimmed">
                      {format(
                        new Date(history.dateCreated),
                        DATE_FORMATS.TIME_FORMAT
                      )}
                    </Text>
                  </Text>
                </Group>
                <Badge
                  size="sm"
                  variant={getJobPlacementStatusVariant(history.status)}
                >
                  {getActionText(history)}
                </Badge>
                {hasShiftDetails && (
                  <Group
                    gap="0"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleExpanded(history.id)}
                  >
                    <Text size="sm">
                      {isExpanded ? 'Hide' : 'Show'} Details
                    </Text>
                    {isExpanded ? (
                      <BiChevronDown size={18} />
                    ) : (
                      <BiChevronRight size={18} />
                    )}
                  </Group>
                )}
              </Group>

              <Collapse in={isExpanded}>{renderShiftDetails(history)}</Collapse>

              <Divider mt="sm" />
            </div>
          );
        })}
      </Stack>
    </Paper>
  );
}
